'use client';

import { useEffect, useRef } from 'react';
import styles from './Waveform.module.css';

export type WaveformSource = (
  emit: (value: number) => void
) => (() => void) | void;

export const Waveform = ({
  source,
  height = 16,
  color = '#fff',
  mirrored = true,
  className,
  ariaLabel,
}: {
  source: WaveformSource;
  height?: number;
  color?: string;
  mirrored?: boolean;
  className?: string;
  ariaLabel?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sourceRef = useRef(source);
  sourceRef.current = source;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;

    let width = 0;
    let dirty = true;
    const resize = () => {
      width = canvas.clientWidth;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      dirty = true;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let values: number[] = [];
    const cleanup = sourceRef.current((value) => {
      values.push(value);
      if (values.length > 4096) values = values.slice(-2048);
      dirty = true;
    });

    let raf = 0;
    const frame = () => {
      raf = requestAnimationFrame(frame);
      if (!dirty) return;
      dirty = false;

      const barCount = Math.floor(width / 2); // 1px bar + 1px gap grid
      const visible = values.slice(-barCount);
      let max = 1e-9;
      for (const v of visible) if (v > max) max = v;

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = color;
      const mid = height / 2;
      for (let i = 0; i < visible.length; i++) {
        const x = width - (visible.length - i) * 2;
        const h = Math.max(1, (visible[i] / max) * (mirrored ? mid : height));
        if (mirrored) ctx.fillRect(x, mid - h, 1, h * 2);
        else ctx.fillRect(x, height - h, 1, h);
      }
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      cleanup?.();
    };
  }, [height, color, mirrored]);

  return (
    <canvas
      ref={canvasRef}
      className={`${styles.canvas}${className ? ` ${className}` : ''}`}
      style={{ height }}
      role="img"
      aria-label={ariaLabel ?? 'Live activity'}
    />
  );
};
